import { useState, useEffect } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { ChevronDown, ListTree, Loader2 } from 'lucide-react';
import { useTask } from '@/hooks/use-tasks';
import type { Task, TaskStatus } from '@/lib/schemas';
import { statusConfig, getStatusLabel } from '../task-table';
import { cn } from '@/lib/utils';
import { useI18n } from '@/lib/i18n';

// ============================================
// KANBAN SUBTASK POPOVER
// ============================================
//
// t15: small chevron button on a Kanban card showing the subtask
// count. Clicking opens a Radix popover listing the subtasks with
// their title + status icon. Each subtask row is clickable and fires
// onSelectSubtask to open the existing task-detail panel.
//
// Subtasks are fetched on-demand via useTask (which returns the
// TaskDetail shape that includes `subtasks: Task[]`). The query is
// cached at taskKeys.detail so subsequent opens are instant.
//
// R1 fix: the previous implementation called `e.preventDefault()` on
// the trigger's onClick. That breaks Radix — PopoverTrigger composes
// its onClick via `composeEventHandlers(props.onClick, onOpenToggle)`
// and skips `onOpenToggle` when `event.defaultPrevented` is true. So
// `e.preventDefault()` silently prevented the popover from ever
// opening, which is why the user saw "nothing happens" on click. We
// keep `e.stopPropagation()` to prevent the click bubbling up to the
// KanbanCard's onClick (which opens the task-detail panel) but drop
// the `preventDefault()` so Radix's toggle handler still runs.

interface KanbanSubtaskPopoverProps {
    taskId: string;
    /** Parent passes its subtaskCount directly so the badge shows
        without waiting for the fetch. */
    subtaskCount: number;
    onSelectSubtask?: (task: Task) => void;
}

export function KanbanSubtaskPopover({ taskId, subtaskCount, onSelectSubtask }: KanbanSubtaskPopoverProps) {
    const { t } = useI18n();
    const [open, setOpen] = useState(false);
    // Lazy fetch — only hits the network when the popover opens. Once
    // opened, the data is cached at taskKeys.detail so reopens are
    // instant and the user can scroll the dropdown freely.
    const { data, isFetching, refetch } = useTask(open ? taskId : '');

    // Refetch every time we open so a freshly-changed subtask set is
    // always reflected. useQuery already deduplicates the network
    // request when the data is fresh, so this is cheap.
    useEffect(() => {
        if (open) refetch();
    }, [open, refetch]);

    if (subtaskCount <= 0) return null;

    const subtasks = (data?.subtasks ?? []) as Task[];

    // t() only accepts a single key, so we interpolate {{count}}
    // ourselves here. Keep this in sync with the i18n string.
    const subtaskButtonLabel = t('kanban.subtaskButton').replace(
        '{{count}}',
        String(subtaskCount),
    );

    return (
        <Popover.Root open={open} onOpenChange={setOpen}>
            <Popover.Trigger asChild>
                <button
                    type="button"
                    aria-label={subtaskButtonLabel}
                    className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[10px] hover:bg-surface-hover/60 transition-colors"
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                        // Don't let the parent's draggable / onClick
                        // (which opens the task-detail panel) steal the
                        // event. We deliberately do NOT call
                        // preventDefault() — Popover.Trigger uses
                        // composeEventHandlers which short-circuits its
                        // own toggle when defaultPrevented is true,
                        // which would silently prevent the popover from
                        // opening. stopPropagation alone is enough to
                        // keep the card click from firing.
                        e.stopPropagation();
                    }}
                >
                    <ListTree className="h-3 w-3" />
                    <span>{subtaskCount}</span>
                    <ChevronDown className="h-2.5 w-2.5 opacity-70" />
                </button>
            </Popover.Trigger>
            <Popover.Portal>
                <Popover.Content
                    side="bottom"
                    align="start"
                    sideOffset={4}
                    // R3: reverted to R1 styling — the user changed their
                    // mind on the R2 transparent body. The opaque
                    // bg-surface + border + arrow combo matches the other
                    // dropdowns in the app (filter bar, columns popover,
                    // etc.) and reads more clearly when many subtasks are
                    // stacked. The hover tint on each row also goes back to
                    // the standard hover:bg-surface-hover (no opacity
                    // modifier) since the popover now has its own opaque
                    // backdrop to sit on.
                    className="z-50 min-w-[220px] max-w-[320px] rounded-md border border-border bg-surface p-1 shadow-lg"
                >
                    <p className="px-2 py-1 text-[10px] uppercase tracking-wide text-foreground-muted">
                        {t('kanban.subtaskPopoverTitle')}
                    </p>
                    {isFetching && subtasks.length === 0 ? (
                        <div className="flex items-center gap-2 px-2 py-2 text-sm text-foreground-muted">
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            {t('common.loading')}
                        </div>
                    ) : subtasks.length === 0 ? (
                        <p className="px-2 py-2 text-xs text-foreground-muted">{t('kanban.subtaskEmpty')}</p>
                    ) : (
                        <ul className="space-y-0.5 max-h-64 overflow-y-auto">
                            {subtasks.map((sub) => {
                                const statusInfo = statusConfig[sub.status as TaskStatus];
                                const StatusIcon = statusInfo?.icon;
                                return (
                                    <li key={sub.id}>
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setOpen(false);
                                                onSelectSubtask?.(sub);
                                            }}
                                            className={cn(
                                                'flex w-full items-center gap-2 px-2 py-1.5 rounded text-left text-sm',
                                                'hover:bg-surface-hover transition-colors'
                                            )}
                                        >
                                            {StatusIcon && (
                                                <StatusIcon className={cn('h-3.5 w-3.5 shrink-0', statusInfo.color)} />
                                            )}
                                            <span className="truncate flex-1">{sub.title}</span>
                                            <span className="text-[10px] uppercase tracking-wide text-foreground-muted shrink-0">
                                                {sub.status ? getStatusLabel(sub.status as TaskStatus, t) : ''}
                                            </span>
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                    <Popover.Arrow className="fill-surface" />
                </Popover.Content>
            </Popover.Portal>
        </Popover.Root>
    );
}
